/**
 * UAMP Events for Robutler
 * Based on UAMP v1.0 specification
 */

import { v4 as uuid } from 'uuid';
import type {
  SessionConfig,
  Capabilities,
  ContentDelta,
  UAMPResponse,
  UAMPError,
  UsageStats,
  Progress,
  Thinking,
  AudioFormat,
  ImageFormat,
} from './types';

export type { AudioFormat };

// Base event structure
export interface BaseEvent {
  type: string;
  event_id: string;
  timestamp?: number;
}

// ============================================
// CLIENT → SERVER EVENTS
// ============================================

export interface SessionCreateEvent extends BaseEvent {
  type: 'session.create';
  uamp_version: string;
  session?: Partial<SessionConfig>;
  client_capabilities?: Partial<Capabilities>;
}

export interface SessionUpdateEvent extends BaseEvent {
  type: 'session.update';
  session: Partial<SessionConfig>;
}

export interface CapabilitiesQueryEvent extends BaseEvent {
  type: 'capabilities.query';
  model?: string;
}

export interface ClientCapabilitiesEvent extends BaseEvent {
  type: 'client.capabilities';
  capabilities: Capabilities;
}

export interface InputTextEvent extends BaseEvent {
  type: 'input.text';
  text: string;
  role?: 'user' | 'system' | 'assistant';
}

export interface InputAudioEvent extends BaseEvent {
  type: 'input.audio';
  audio: string; // Base64
  format?: AudioFormat;
  is_final?: boolean;
}

export interface InputImageEvent extends BaseEvent {
  type: 'input.image';
  image: string | { url: string };
  format?: ImageFormat;
  detail?: 'low' | 'high' | 'auto';
}

export interface InputVideoEvent extends BaseEvent {
  type: 'input.video';
  video: string | { url: string };
  format?: string;
}

export interface InputFileEvent extends BaseEvent {
  type: 'input.file';
  file: string | { url: string };
  filename: string;
  mime_type: string;
}

export interface ResponseCreateEvent extends BaseEvent {
  type: 'response.create';
  response?: Partial<SessionConfig>;
}

export interface ResponseCancelEvent extends BaseEvent {
  type: 'response.cancel';
  response_id?: string;
}

export interface ToolResultEvent extends BaseEvent {
  type: 'tool.result';
  call_id: string;
  result: string; // JSON string
  is_error?: boolean;
}

export interface PingEvent extends BaseEvent {
  type: 'ping';
}

/**
 * Reply uplink for the `workspace.widgets.*` RPC. Emitted by
 * `<WorkspaceAgentCommandReceiver/>` after it has dispatched an
 * `agent.command` frame (received via `workspace.bus` fan-out)
 * through the local `WidgetCommandBus`. The WS server routes the
 * reply back into `requestWidgetCommand`'s pending Promise via
 * `resolveWidgetCommandReply`. See
 * `lib/realtime/workspace-agent-rpc.ts` for the protocol shape.
 */
export interface WorkspaceBusReplyEvent extends BaseEvent {
  type: 'workspace.bus.reply';
  workspaceId: string;
  requestId: string;
  ok: boolean;
  result?: unknown;
  error?: { code: string; message: string };
}

// Typing indicator (client → server)
export interface InputTypingEvent extends BaseEvent {
  type: 'input.typing';
  is_typing: boolean;
  chat_id?: string;
  /** @deprecated Use chat_id */
  conversation_id?: string;
}

// Payment submission (client → server)
export interface PaymentSubmitEvent extends BaseEvent {
  type: 'payment.submit';
  payment: {
    scheme: string;
    amount: string;
    network?: string;
    token?: string;
    proof?: string;
    ap2_credential?: Record<string, unknown>;
  };
}

// Union of all client events
export type ClientEvent =
  | SessionCreateEvent
  | SessionUpdateEvent
  | CapabilitiesQueryEvent
  | ClientCapabilitiesEvent
  | InputTextEvent
  | InputAudioEvent
  | InputImageEvent
  | InputVideoEvent
  | InputFileEvent
  | InputTypingEvent
  | ResponseCreateEvent
  | ResponseCancelEvent
  | ToolResultEvent
  | PaymentSubmitEvent
  | PingEvent
  | WorkspaceBusReplyEvent;

// ============================================
// SERVER → CLIENT EVENTS
// ============================================

export interface SessionCreatedEvent extends BaseEvent {
  type: 'session.created';
  uamp_version: string;
  session: SessionConfig;
}

export interface SessionUpdatedEvent extends BaseEvent {
  type: 'session.updated';
  session: SessionConfig;
}

export interface CapabilitiesEvent extends BaseEvent {
  type: 'capabilities';
  capabilities: Capabilities;
}

export interface ResponseCreatedEvent extends BaseEvent {
  type: 'response.created';
  response_id: string;
}

export interface ResponseDeltaEvent extends BaseEvent {
  type: 'response.delta';
  response_id: string;
  delta: ContentDelta;
}

export interface ResponseDoneEvent extends BaseEvent {
  type: 'response.done';
  response_id: string;
  response: UAMPResponse;
}

export interface ResponseErrorEvent extends BaseEvent {
  type: 'response.error';
  error: UAMPError;
  response_id?: string;
}

export interface ResponseCancelledEvent extends BaseEvent {
  type: 'response.cancelled';
  response_id?: string;
}

export interface ToolCallEvent extends BaseEvent {
  type: 'tool.call';
  call_id: string;
  name: string;
  arguments: string; // JSON string
}

export interface ToolCallDoneEvent extends BaseEvent {
  type: 'tool.call_done';
  call_id: string;
}

export interface AudioDeltaEvent extends BaseEvent {
  type: 'audio.delta';
  audio: string; // Base64 chunk
}

export interface TranscriptDeltaEvent extends BaseEvent {
  type: 'transcript.delta';
  text: string;
  is_final?: boolean;
}

export interface ProgressEventPayload extends BaseEvent {
  type: 'progress';
  progress: Progress;
}

export interface ThinkingEventPayload extends BaseEvent {
  type: 'thinking';
  thinking: Thinking;
}

export interface UsageDeltaEvent extends BaseEvent {
  type: 'usage.delta';
  usage: UsageStats;
}

export interface PongEvent extends BaseEvent {
  type: 'pong';
}

export interface RateLimitEvent extends BaseEvent {
  type: 'rate_limit';
  limit: number;
  remaining: number;
  reset_at: string; // ISO 8601
}

// Typing indicator from another participant (server → client)
export interface PresenceTypingEvent extends BaseEvent {
  type: 'presence.typing';
  user_id: string;
  username?: string;
  is_typing: boolean;
  chat_id?: string;
  /** @deprecated Use chat_id */
  conversation_id?: string;
}

// Payment required (server → client)
export interface PaymentRequiredEvent extends BaseEvent {
  type: 'payment.required';
  response_id?: string;
  requirements: {
    amount: string;
    currency: string;
    schemes: Array<{
      scheme: 'token' | 'crypto' | 'card' | 'ap2';
      network?: string;
      address?: string;
      min_amount?: string;
      max_amount?: string;
    }>;
    expires_at?: number;
    reason?: string;
    ap2?: {
      mandate_uri?: string;
      credential_types?: string[];
      checkout_session_uri?: string;
    };
  };
}

// Payment accepted (server → client)
export interface PaymentAcceptedEvent extends BaseEvent {
  type: 'payment.accepted';
  payment_id: string;
  balance_remaining?: string;
  expires_at?: number;
}

// Balance update (server → client)
export interface PaymentBalanceEvent extends BaseEvent {
  type: 'payment.balance';
  balance: string;
  currency: string;
  low_balance_warning?: boolean;
  estimated_remaining?: number;
  expires_at?: number;
}

// Payment error (server → client)
export interface PaymentErrorEvent extends BaseEvent {
  type: 'payment.error';
  code: 'insufficient_balance' | 'token_expired' | 'token_invalid' | 'payment_failed' | 'rate_limited' | 'mandate_revoked';
  message: string;
  balance_required?: string;
  balance_current?: string;
  can_retry: boolean;
}

// New chat message (server → client); message payload matches ChatMessage shape
export interface MessageCreatedEvent extends BaseEvent {
  type: 'message.created';
  message: Record<string, unknown>;
}

export interface MessageUpdatedEvent extends BaseEvent {
  type: 'message.updated';
  message: Record<string, unknown>;
}

export interface MessageDeletedEvent extends BaseEvent {
  type: 'message.deleted';
  message_id: string;
  chat_id: string;
}

// New chat (server → client). Emitted when a chat is born outside the
// owner's direct action — primarily bridge inbound (Telegram/etc) where the
// chat list needs to add the row in real time without waiting for the user
// to navigate to the chats page.
export interface ChatCreatedEvent extends BaseEvent {
  type: 'chat.created';
  chat: Record<string, unknown>;
}

/**
 * v3 Plan 1 — `live.end` server event.
 *
 * Emitted by the producing agent (or by the portal on behalf of a
 * cleanup) when a `live` block stops broadcasting. Late-joining
 * clients that subscribe AFTER `live.end` MUST replay this event from
 * the per-message UAMP event store and surface the block as ended.
 *
 * `final_capture` is an optional snapshot to render in lieu of the
 * live wire (e.g. last frame of a browser session). When present the
 * receiver SHOULD display it as the captured-form replacement
 * (display_hint='compact-chip' is typical for ended browser streams).
 */
export interface LiveEndEvent extends BaseEvent {
  type: 'live.end';
  live_id: string;
  message_id?: string;
  reason?:
    | 'producer_finished'
    | 'producer_disconnected'
    | 'expired'
    | 'cancelled'
    | 'error';
  final_capture?: {
    capturedAt: string;
    thumbnailUrl?: string;
  };
}

// Union of all server events
export type ServerEvent =
  | SessionCreatedEvent
  | SessionUpdatedEvent
  | CapabilitiesEvent
  | ResponseCreatedEvent
  | ResponseDeltaEvent
  | ResponseDoneEvent
  | ResponseErrorEvent
  | ResponseCancelledEvent
  | ToolCallEvent
  | ToolCallDoneEvent
  | AudioDeltaEvent
  | TranscriptDeltaEvent
  | ProgressEventPayload
  | ThinkingEventPayload
  | UsageDeltaEvent
  | PresenceTypingEvent
  | PaymentRequiredEvent
  | PaymentAcceptedEvent
  | PaymentBalanceEvent
  | PaymentErrorEvent
  | PongEvent
  | RateLimitEvent
  | MessageCreatedEvent
  | MessageUpdatedEvent
  | MessageDeletedEvent
  | ChatCreatedEvent
  | LiveEndEvent;

// All events
export type UAMPEvent = ClientEvent | ServerEvent;

// ============================================
// EVENT FACTORIES
// ============================================

function createEventId(): string {
  return `evt_${uuid().replace(/-/g, '').slice(0, 12)}`;
}

function baseEvent(type: string): { type: string; event_id: string; timestamp: number } {
  return {
    type,
    event_id: createEventId(),
    timestamp: Date.now(),
  };
}

// Client event factories
export function createSessionCreate(
  uampVersion: string,
  session?: Partial<SessionConfig>,
  clientCapabilities?: Partial<Capabilities>
): SessionCreateEvent {
  return {
    ...baseEvent('session.create'),
    type: 'session.create',
    uamp_version: uampVersion,
    session,
    client_capabilities: clientCapabilities,
  };
}

export function createInputText(text: string, role?: 'user' | 'system' | 'assistant'): InputTextEvent {
  return {
    ...baseEvent('input.text'),
    type: 'input.text',
    text,
    role,
  };
}

export function createInputAudio(audio: string, format?: AudioFormat, isFinal?: boolean): InputAudioEvent {
  return {
    ...baseEvent('input.audio'),
    type: 'input.audio',
    audio,
    format,
    is_final: isFinal,
  };
}

export function createInputImage(
  image: string | { url: string },
  format?: ImageFormat,
  detail?: 'low' | 'high' | 'auto'
): InputImageEvent {
  return {
    ...baseEvent('input.image'),
    type: 'input.image',
    image,
    format,
    detail,
  };
}

export function createResponseCreate(response?: Partial<SessionConfig>): ResponseCreateEvent {
  return {
    ...baseEvent('response.create'),
    type: 'response.create',
    response,
  };
}

export function createToolResult(callId: string, result: string, isError?: boolean): ToolResultEvent {
  return {
    ...baseEvent('tool.result'),
    type: 'tool.result',
    call_id: callId,
    result,
    is_error: isError,
  };
}

export function createPing(): PingEvent {
  return {
    ...baseEvent('ping'),
    type: 'ping',
  };
}

// Server event factories
export function createSessionCreated(
  uampVersion: string,
  session: SessionConfig
): SessionCreatedEvent {
  return {
    ...baseEvent('session.created'),
    type: 'session.created',
    uamp_version: uampVersion,
    session,
  };
}

export function createCapabilities(capabilities: Capabilities): CapabilitiesEvent {
  return {
    ...baseEvent('capabilities'),
    type: 'capabilities',
    capabilities,
  };
}

export function createResponseCreated(responseId: string): ResponseCreatedEvent {
  return {
    ...baseEvent('response.created'),
    type: 'response.created',
    response_id: responseId,
  };
}

export function createResponseDelta(responseId: string, delta: ContentDelta): ResponseDeltaEvent {
  return {
    ...baseEvent('response.delta'),
    type: 'response.delta',
    response_id: responseId,
    delta,
  };
}

export function createResponseDone(responseId: string, response: UAMPResponse): ResponseDoneEvent {
  return {
    ...baseEvent('response.done'),
    type: 'response.done',
    response_id: responseId,
    response,
  };
}

export function createResponseError(error: UAMPError, responseId?: string): ResponseErrorEvent {
  return {
    ...baseEvent('response.error'),
    type: 'response.error',
    error,
    response_id: responseId,
  };
}

export function createResponseCancelled(responseId?: string): ResponseCancelledEvent {
  return {
    ...baseEvent('response.cancelled'),
    type: 'response.cancelled',
    response_id: responseId,
  };
}

export function createToolCall(callId: string, name: string, args: string): ToolCallEvent {
  return {
    ...baseEvent('tool.call'),
    type: 'tool.call',
    call_id: callId,
    name,
    arguments: args,
  };
}

export function createAudioDelta(audio: string): AudioDeltaEvent {
  return {
    ...baseEvent('audio.delta'),
    type: 'audio.delta',
    audio,
  };
}

export function createTranscriptDelta(text: string, isFinal?: boolean): TranscriptDeltaEvent {
  return {
    ...baseEvent('transcript.delta'),
    type: 'transcript.delta',
    text,
    is_final: isFinal,
  };
}

export function createProgress(progress: Progress): ProgressEventPayload {
  return {
    ...baseEvent('progress'),
    type: 'progress',
    progress,
  };
}

export function createThinking(thinking: Thinking): ThinkingEventPayload {
  return {
    ...baseEvent('thinking'),
    type: 'thinking',
    thinking,
  };
}

export function createPong(): PongEvent {
  return {
    ...baseEvent('pong'),
    type: 'pong',
  };
}

export function createMessageCreated(message: Record<string, unknown>): MessageCreatedEvent {
  return {
    ...baseEvent('message.created'),
    type: 'message.created',
    message,
  };
}

export function createChatCreated(chat: Record<string, unknown>): ChatCreatedEvent {
  return {
    ...baseEvent('chat.created'),
    type: 'chat.created',
    chat,
  };
}

export function createMessageUpdated(message: Record<string, unknown>): MessageUpdatedEvent {
  return {
    ...baseEvent('message.updated'),
    type: 'message.updated',
    message,
  };
}

export function createMessageDeleted(messageId: string, chatId: string): MessageDeletedEvent {
  return {
    ...baseEvent('message.deleted'),
    type: 'message.deleted',
    message_id: messageId,
    chat_id: chatId,
  };
}

// Typing indicator factories
export function createInputTyping(isTyping: boolean, chatId?: string): InputTypingEvent {
  return {
    ...baseEvent('input.typing'),
    type: 'input.typing',
    is_typing: isTyping,
    chat_id: chatId,
  };
}

export function createPresenceTyping(
  userId: string,
  isTyping: boolean,
  username?: string,
  chatId?: string
): PresenceTypingEvent {
  return {
    ...baseEvent('presence.typing'),
    type: 'presence.typing',
    user_id: userId,
    is_typing: isTyping,
    username,
    chat_id: chatId,
  };
}

// Payment event factories
export function createPaymentRequired(
  requirements: PaymentRequiredEvent['requirements'],
  responseId?: string
): PaymentRequiredEvent {
  return {
    ...baseEvent('payment.required'),
    type: 'payment.required',
    response_id: responseId,
    requirements,
  };
}

export function createPaymentSubmit(
  payment: PaymentSubmitEvent['payment']
): PaymentSubmitEvent {
  return {
    ...baseEvent('payment.submit'),
    type: 'payment.submit',
    payment,
  };
}

export function createPaymentAccepted(
  paymentId: string,
  balanceRemaining?: string,
  expiresAt?: number
): PaymentAcceptedEvent {
  return {
    ...baseEvent('payment.accepted'),
    type: 'payment.accepted',
    payment_id: paymentId,
    balance_remaining: balanceRemaining,
    expires_at: expiresAt,
  };
}

export function createPaymentBalance(
  balance: string,
  currency: string,
  options?: {
    lowBalanceWarning?: boolean;
    estimatedRemaining?: number;
    expiresAt?: number;
  }
): PaymentBalanceEvent {
  return {
    ...baseEvent('payment.balance'),
    type: 'payment.balance',
    balance,
    currency,
    low_balance_warning: options?.lowBalanceWarning,
    estimated_remaining: options?.estimatedRemaining,
    expires_at: options?.expiresAt,
  };
}

export function createPaymentError(
  code: PaymentErrorEvent['code'],
  message: string,
  canRetry: boolean,
  balanceRequired?: string,
  balanceCurrent?: string
): PaymentErrorEvent {
  return {
    ...baseEvent('payment.error'),
    type: 'payment.error',
    code,
    message,
    can_retry: canRetry,
    balance_required: balanceRequired,
    balance_current: balanceCurrent,
  };
}

// ============================================
// EVENT PARSING & VALIDATION
// ============================================

const CLIENT_EVENT_TYPES = new Set([
  'session.create',
  'session.update',
  'capabilities.query',
  'client.capabilities',
  'input.text',
  'input.audio',
  'input.image',
  'input.video',
  'input.file',
  'input.typing',
  'response.create',
  'response.cancel',
  'tool.result',
  'payment.submit',
  'ping',
]);

const SERVER_EVENT_TYPES = new Set([
  'session.created',
  'session.updated',
  'capabilities',
  'response.created',
  'response.delta',
  'response.done',
  'response.error',
  'tool.call',
  'tool.call_done',
  'audio.delta',
  'transcript.delta',
  'progress',
  'thinking',
  'usage.delta',
  'presence.typing',
  'payment.required',
  'payment.accepted',
  'payment.balance',
  'payment.error',
  'pong',
  'rate_limit',
  'message.created',
  'message.updated',
  'message.deleted',
  'chat.created',
]);

export function isClientEvent(event: UAMPEvent): event is ClientEvent {
  return CLIENT_EVENT_TYPES.has(event.type);
}

export function isServerEvent(event: UAMPEvent): event is ServerEvent {
  return SERVER_EVENT_TYPES.has(event.type);
}

export function parseEvent(data: string | object): UAMPEvent | null {
  try {
    const obj = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (!obj || typeof obj !== 'object' || !obj.type || !obj.event_id) {
      return null;
    }

    return obj as UAMPEvent;
  } catch {
    return null;
  }
}

export function serializeEvent(event: UAMPEvent): string {
  return JSON.stringify(event);
}
